import { useState, useEffect, useRef } from 'react'

import { type FilterTag, useModal } from '@procurify/ui'
import { unstable_batchedUpdates } from 'react-dom'

import {
  type CreateSavedFilterSchema,
  SavedFilterAPI,
} from '@webapp/api/SavedFilterAPI'
import { type FilterManagerContextValues } from '@webapp/components/FilterManager/FilterManagerContext'
import { type FilterManagerFilter } from '@webapp/components/FilterManager/types'
import { toast } from '@webapp/components/Notification'
import { type ISavedFiltersFormProps } from '@webapp/components/SavedFiltersForm/SavedFiltersForm'
import { SavedFiltersFormModal } from '@webapp/components/SavedFiltersForm/SavedFiltersFormModal'
import { type SavedFilter } from '@webapp/models/SavedFilter'
import messages from './messages'
import {
  type SavedFilterCollection,
  type SavedFilterCollectionSchemas,
} from './types'
import { useProcurifyIntl } from '../useProcurifyIntl'

export interface UseSavedFilterManagerOptions<
  CollectionId extends SavedFilterCollection,
  Filters extends { [key: string]: any },
  CollectionSchema extends SavedFilterCollectionSchemas[CollectionId],
> {
  collectionId: CollectionId
  filterManager: FilterManagerContextValues<Filters>
  /**
   * Objects of functions to decode saved filter shape
   * into the expected form control's shape
   */
  decodeFilters: {
    [key in keyof Filters & keyof CollectionSchema]: (
      savedValue: NonNullable<CollectionSchema[key]>,
      params: Partial<SavedFilterCollectionSchemas[CollectionId]>
    ) => FilterManagerFilter<Filters[key]>
  }
  /**
   * Objects of functions to encode form control's shape
   * into the expected saved filter shape
   */
  encodeFilters: {
    [key in keyof Filters & keyof CollectionSchema]: (
      filterValue: NonNullable<FilterManagerFilter<Filters[key]>['value']>
    ) => CollectionSchema[key]
  }
  /**
   * An array of keys that are allowed to be saved. All keys
   * are allowed if this is undefined.
   *
   * If a disallowed key is in the filters, then `hasValidFilters` will be `false`
   */
  allowedFilters?: (keyof Filters)[]
  /**
   * Values saved beside the encoded filters, for a scope that is not a
   * filter (the selected entity). They are not decoded on apply; read them
   * in `beforeApply`.
   */
  additionalSavedValues?: Partial<CollectionSchema>
  /**
   * Runs before a saved filter is applied. `false` applies nothing. Return a
   * plain boolean when no confirmation is needed, so the apply stays inside
   * the click handler and React batches it into one render.
   */
  beforeApply?: (
    savedFilters: SavedFilter<CollectionSchema>['filters']
  ) => boolean | Promise<boolean>
  /**
   * Display-only tags for the save and edit modals, built from the values
   * saved beside the filters: `additionalSavedValues` on save, the stored
   * values on edit. Never part of the payload.
   */
  getScopeTags?: (savedValues: Partial<CollectionSchema>) => FilterTag[]
}

export interface UseSavedFilterManager<
  CollectionId extends SavedFilterCollection,
  Filters extends { [key: string]: any },
  CollectionSchema extends SavedFilterCollectionSchemas[CollectionId],
> {
  collectionId: SavedFilterCollection
  hasValidFilters: boolean
  hasFilters: boolean
  selectedSavedFilter: SavedFilter
  setSelectedSavedFilter: (savedFilter: SavedFilter) => void
  /**
   * Opens the saved filter modal to let the user save
   * the current selected filters
   */
  saveFilters: () => void
  /**
   * Applies the saved filters to the filter manager. `false` when
   * `beforeApply` declined, or while an earlier apply still awaits its
   * `beforeApply`. A promise only when `beforeApply` returned one.
   */
  applySavedFilters: (
    savedFilters: SavedFilter<CollectionSchema>['filters']
  ) => boolean | Promise<boolean>
  getScopeTags?: (savedValues: Partial<CollectionSchema>) => FilterTag[]
  filterManager: FilterManagerContextValues<Filters>
}

export function useSavedFilterManager<
  CollectionId extends SavedFilterCollection,
  Filters extends { [key: string]: any },
  CollectionSchema extends SavedFilterCollectionSchemas[CollectionId],
>({
  filterManager,
  collectionId,
  decodeFilters,
  encodeFilters,
  allowedFilters,
  additionalSavedValues,
  beforeApply,
  getScopeTags,
}: UseSavedFilterManagerOptions<
  CollectionId,
  Filters,
  CollectionSchema
>): UseSavedFilterManager<CollectionId, Filters, CollectionSchema> {
  const modal = useModal()
  const intl = useProcurifyIntl()

  const [selectedSavedFilter, setSelectedSavedFilter] =
    useState<SavedFilter>(null)

  const { filterValues, filterTags } = filterManager

  // An awaited `beforeApply` resumes after other renders; the manager it
  // closed over may be stale by then.
  const filterManagerRef = useRef(filterManager)
  filterManagerRef.current = filterManager

  const isApplyingRef = useRef(false)

  const isValid = Array.isArray(allowedFilters)
    ? !Object.keys(filterValues).some(
        (filterKey) => allowedFilters.indexOf(filterKey) < 0
      )
    : true

  const hasFilters = Object.values(filterValues).some((val) => !!val)

  // An awaited `beforeApply` may drop filters before the saved ones are
  // added; the selection the user just made must survive that window.
  useEffect(() => {
    if (!hasFilters && !isApplyingRef.current) {
      setSelectedSavedFilter(null)
    }
  }, [hasFilters])

  const onSubmitSaveFilters: ISavedFiltersFormProps['onSubmit'] = (
    value,
    formikActions
  ) => {
    formikActions.setSubmitting(true)

    const data: CreateSavedFilterSchema = {
      ...value,
      filters: {},
    }

    data.filters = Object.keys(filterValues).reduce((prev, filterKey) => {
      const filterValue = filterValues[filterKey]

      // A key can be present with an undefined value — resolvers that fail to
      // match (a deep-linked id that no longer exists) end in `?? undefined`.
      // encodeFilters is typed NonNullable, so guard it like applySavedFilters
      // guards decode.
      if (
        encodeFilters[filterKey] &&
        filterValue !== undefined &&
        filterValue !== null
      ) {
        prev[filterKey] = encodeFilters[filterKey](filterValue)
      } else {
        prev[filterKey] = filterValue
      }
      return prev
    }, {})

    Object.assign(data.filters, additionalSavedValues)

    SavedFilterAPI.saveFilters(data)
      .then((res) => res.data)
      .then((newSavedFilter) => {
        formikActions.setSubmitting(false)

        setSelectedSavedFilter(newSavedFilter)

        modal.hideModal()

        toast.success({
          title: intl.formatMessage(messages.notificationSuccessTitle),
          children: intl.formatMessage(messages.notificationSaveSuccessMessage),
        })
      })
      .catch(() => {
        formikActions.setSubmitting(false)
      })
  }

  const saveFilters: UseSavedFilterManager<
    CollectionId,
    Filters,
    CollectionSchema
  >['saveFilters'] = () => {
    modal.showModal(SavedFiltersFormModal, {
      open: true,
      collection: collectionId,
      filters: filterTags,
      scopeTags:
        getScopeTags && additionalSavedValues
          ? getScopeTags(additionalSavedValues)
          : [],
      onSubmit: onSubmitSaveFilters,
    })
  }

  const applySavedFilters: UseSavedFilterManager<
    CollectionId,
    Filters,
    CollectionSchema
  >['applySavedFilters'] = (savedFilters) => {
    if (isApplyingRef.current) return false

    const applyFilters = () => {
      const { clearFilters, addFilter } = filterManagerRef.current

      clearFilters()

      Object.keys(savedFilters).forEach((savedFilterKey) => {
        const decodeFilterFn = decodeFilters[savedFilterKey]
        const savedFilterData = savedFilters[savedFilterKey]

        if (
          decodeFilterFn &&
          savedFilterData !== undefined &&
          savedFilterData !== null
        )
          addFilter(decodeFilterFn(savedFilterData, savedFilters))
      })
    }

    const verdict = beforeApply ? beforeApply(savedFilters) : true

    if (typeof verdict === 'boolean') {
      if (verdict) applyFilters()

      return verdict
    }

    isApplyingRef.current = true

    return verdict
      .catch(() => false)
      .then((isAllowed) => {
        // After the await React 17 renders once per update, so the clear and
        // the adds go in one batch: one request.
        if (isAllowed) unstable_batchedUpdates(applyFilters)

        return isAllowed
      })
      .finally(() => {
        isApplyingRef.current = false
      })
  }

  return {
    collectionId,
    hasValidFilters: isValid,
    selectedSavedFilter,
    setSelectedSavedFilter,
    hasFilters,
    saveFilters,
    applySavedFilters,
    getScopeTags,
    filterManager,
  }
}
